import React from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Text } from '@weave/design-system';

export type StatusBannerVariant = 'success' | 'blocked' | 'warning' | 'exception_noted';

export interface StatusBannerProps {
  variant: StatusBannerVariant;
  icon: React.ReactNode;
  title: string;
  description?: string | string[];
  action?: React.ReactNode;
}

const VARIANT_CONFIG: Record<StatusBannerVariant, { bg: string }> = {
  success: {
    bg: '#E5FBEE',
  },
  blocked: {
    bg: '#FFF1F0',
  },
  warning: {
    bg: '#FFF6D6',
  },
  exception_noted: {
    bg: '#FCF4E4',
  },
};

export const StatusBanner = ({
  variant,
  icon,
  title,
  description,
  action,
}: StatusBannerProps) => {
  const config = VARIANT_CONFIG[variant];

  const renderDescription = () => {
    if (!description) return null;

    if (Array.isArray(description)) {
      return (
        <ul css={bulletList}>
          {description.map((item, index) => (
            <Text as="li" key={`${item}-${index}`} size="small">
              {item}
            </Text>
          ))}
        </ul>
      );
    }

    return (
      <Text size="small" css={{ margin: 0 }}>
        {description}
      </Text>
    );
  };

  return (
    <div css={[bannerBase, bannerBackground(config.bg)]}>
      <div css={bannerContent}>
        {icon}
        <div css={bannerTitleDescription}>
          <Text weight="bold" css={bannerTitle} size="medium">
            {title}
          </Text>
          {renderDescription()}
        </div>
      </div>
      {action && <div css={actionWrapper}>{action}</div>}
    </div>
  );
};

const bannerBase = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.small};
  min-height: 72px;
  box-shadow: ${theme.shadows.header};
`;

const bannerBackground = (bg: string) => css`
  background-color: ${bg};
`;

const bannerContent = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};
`;

const bannerTitle = css`
  margin: 0;
`;

const bulletList = css`
  list-style-type: disc;
  padding-left: ${theme.spacing(2)};
  margin: 0;
`;

const bannerTitleDescription = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`;

const actionWrapper = css`
  display: flex;
  align-items: center;
`;
